<template>
  <div class="ai-set">
    <APageHead
      title="AI 설정"
      sub="AI 를 쓰는 기능별로 어느 AI 서버·모델을 쓸지 정합니다. 저장하면 재배포 없이 반영됩니다."
    >
      <template #actions>
        <ABtn kind="ghost" icon="refresh" :loading="loading" @click="reloadAll">새로고침</ABtn>
        <ABtn v-if="tab === 'profiles'" kind="primary" icon="add" @click="openCreate">프로필 등록</ABtn>
      </template>
    </APageHead>

    <!--
      config_source=bootstrap 은 서버가 설정 DB 를 읽지 못해 비상 기본값(사내 GPU 서버만)으로
      돌고 있다는 뜻입니다. 이 동안은 화면에서 바꾼 설정이 실제 호출에 쓰이지 않으므로 최상단에 띄웁니다.
    -->
    <div v-if="status && status.config_source === 'bootstrap'" class="ai-set__banner ai-set__banner--err">
      <i class="material-icons-round">gpp_maybe</i>
      <div>
        <b>서버가 설정 DB 를 읽지 못하고 비상 기본값으로 돌고 있습니다.</b>
        <span>
          지금은 사내 GPU 서버만 사용하며, 이 화면의 설정이 실제 AI 호출에 적용되지 않습니다.
          배포 직후 1분 이내라면 곧 사라집니다. 계속 보이면 개발자에게 알려 주세요.
        </span>
      </div>
    </div>

    <!--
      상태 조회가 실패하면 bootstrap 경고도 예비 전환 경고도 뜨지 않습니다.
      "경고가 없다" 와 "확인하지 못했다" 를 구분해 주어야 관리자가 정상으로 오해하지 않습니다.
    -->
    <div v-if="statusError" class="ai-set__banner ai-set__banner--warn">
      <i class="material-icons-round">cloud_off</i>
      <div>
        <b>AI 호출 상태를 확인하지 못했습니다.</b>
        <span>{{ statusError }} — 예비 서버 전환·비상 기본값 경고가 표시되지 않는 상태입니다. 새로고침해 주세요.</span>
      </div>
    </div>

    <!-- role=fallback 에 성공이 잡히면 1순위가 죽어 예비 서버로 돌고 있다는 뜻입니다(docs/AI설정_관리자화면_가이드.md §2-4). -->
    <div v-if="fallbackRunning.length" class="ai-set__banner ai-set__banner--warn">
      <i class="material-icons-round">swap_horiz</i>
      <div>
        <b>지금 예비 서버로 전환되어 돌고 있습니다.</b>
        <span>
          최근 {{ windowMin }}분 동안
          <b v-for="(r, i) in fallbackRunning" :key="r.feature + r.profile_code">
            {{ i > 0 ? ', ' : '' }}{{ featureLabel(r.feature) }} → {{ r.profile_code }}({{ r.success_count }}건)
          </b>
          이 예비 프로필로 처리됐습니다. 1순위 서버 상태를 확인하세요.
        </span>
      </div>
    </div>

    <div class="p-tabs ai-set__tabs">
      <button
        v-for="t in TABS"
        :key="t.id"
        type="button"
        :class="['p-tabs__tab', { 'is-active': tab === t.id }]"
        @click="tab = t.id"
      >
        <i class="material-icons-round ai-set__tab-icon">{{ t.icon }}</i>
        {{ t.label }}
      </button>
    </div>

    <!-- ───────────────────── 기능별 설정 ───────────────────── -->
    <template v-if="tab === 'features'">
      <div v-if="loading && !features.length" class="ai-set__loading">
        <div class="p-spinner p-spinner--lg"></div>
        <p>설정을 불러오는 중...</p>
      </div>

      <ACard v-for="f in FEATURES" v-else :key="f.code">
        <template #title>
          <span class="ai-set__sec-title">
            <i class="material-icons-round ai-set__sec-icon">{{ f.icon }}</i>
            {{ f.label }}
          </span>
        </template>
        <template #sub>{{ f.desc }}</template>
        <template #action>
          <APill v-if="isDirty(f.code)" kind="yellow" dot>수정 중</APill>
          <span v-else-if="draft(f.code)" class="ai-set__updated">
            {{ fmtClock(draft(f.code).updated_at) }} · {{ updatedByLabel(draft(f.code).updated_by) }}
          </span>
        </template>

        <div v-if="draft(f.code)" class="ai-set__form">
          <div class="ai-set__grid">
            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round p-field__label-icon">looks_one</i>
                1순위 프로필 <span class="p-field__req">*</span>
              </label>
              <select
                :value="draft(f.code).primary_profile"
                class="p-input"
                @change="onPrimaryChange(f.code, $event.target.value)"
              >
                <!--
                  1순위가 빈 값이면 어떤 option 과도 맞지 않아 브라우저가 첫 프로필을 고른 것처럼
                  보여 줍니다. 화면과 저장값이 어긋나지 않도록 빈 값 자리를 만들어 둡니다.
                -->
                <option v-if="!draft(f.code).primary_profile" value="" disabled>선택하세요</option>
                <option
                  v-for="p in profileOptions(f.code, 'primary')"
                  :key="p.profile_code"
                  :value="p.profile_code"
                  :disabled="isProfileBlocked(f.code, p)"
                >
                  {{ profileOptionLabel(f.code, p) }}
                </option>
              </select>
              <div class="p-field__hint"><i>info</i>먼저 호출할 프로필입니다.</div>
            </div>

            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round p-field__label-icon">looks_two</i>
                예비(폴백) 프로필
              </label>
              <select v-model="draft(f.code).fallback_profile" class="p-input">
                <option value="">없음</option>
                <option
                  v-for="p in profileOptions(f.code, 'fallback')"
                  :key="p.profile_code"
                  :value="p.profile_code"
                  :disabled="isProfileBlocked(f.code, p)"
                >
                  {{ profileOptionLabel(f.code, p) }}
                </option>
              </select>
              <div class="p-field__hint"><i>info</i>1순위가 실패하면 자동으로 넘어갑니다.</div>
            </div>

            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round p-field__label-icon">smart_toy</i>
                모델
              </label>
              <input
                v-model.trim="draft(f.code).model"
                class="p-input ai-set__mono"
                type="text"
                :placeholder="primaryDefaultModel(f.code)"
              />
              <div class="p-field__hint"><i>info</i>보통 비워 둡니다. 비우면 1순위 프로필의 기본 모델을 씁니다.</div>
            </div>

            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round p-field__label-icon">thermostat</i>
                temperature
              </label>
              <input
                v-model="draft(f.code).temperature"
                class="p-input"
                type="number"
                min="0"
                max="2"
                step="0.01"
                placeholder="서버 기본값"
              />
              <div class="p-field__hint"><i>info</i>0~2, 소수 2자리. 비우면 서버 기본값.</div>
            </div>

            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round p-field__label-icon">format_list_numbered</i>
                max_tokens
              </label>
              <input
                v-model="draft(f.code).max_tokens"
                class="p-input"
                type="number"
                min="1"
                max="32000"
                step="1"
                placeholder="제한 없음"
              />
              <div class="p-field__hint"><i>info</i>1~32000. 비우면 제한 없음.</div>
            </div>

            <div class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round p-field__label-icon">timer</i>
                응답 대기 시간 (초) <span class="p-field__req">*</span>
              </label>
              <input
                v-model="draft(f.code).timeout_sec"
                class="p-input"
                type="number"
                min="5"
                max="600"
                step="1"
              />
              <div class="p-field__hint">
                <i>info</i>5~600초.
                {{ draft(f.code).fallback_profile
                  ? `예비가 있어 최악의 경우 ${(Number(draft(f.code).timeout_sec) || 0) * 2}초까지 걸립니다.`
                  : '예비가 없으면 이 시간이 최대 대기 시간입니다.' }}
              </div>
            </div>
          </div>

          <div class="ai-set__toggle">
            <button
              type="button"
              :class="['p-switch', { 'is-on': draft(f.code).allow_cloud }]"
              @click="draft(f.code).allow_cloud = !draft(f.code).allow_cloud"
            ></button>
            <div>
              <b>과금 프로필 사용 허용</b>
              <span>
                끄면 과금되는 프로필을 이 기능에 지정할 수 없습니다.
                학생 리포트 요약은 학생 수만큼 호출이 늘어 요금이 커질 수 있어 기본적으로 꺼 둡니다.
              </span>
            </div>
          </div>

          <!-- 서버가 409 로 거부할 조합을 저장 전에 알려 왕복을 줄입니다(docs/AI설정_관리자화면_가이드.md §2-1). -->
          <div v-if="cloudConflict(f.code)" class="ai-set__alert ai-set__alert--warn">
            <i class="material-icons-round">report_problem</i>
            <span>{{ cloudConflict(f.code) }}</span>
          </div>

          <div class="ai-set__form-foot">
            <ABtn
              kind="ghost"
              icon="undo"
              :disabled="!isDirty(f.code)"
              @click="resetDraft(f.code)"
            >되돌리기</ABtn>
            <ABtn
              kind="primary"
              icon="save"
              :loading="savingFeature === f.code"
              :disabled="!isDirty(f.code) || !!cloudConflict(f.code)"
              @click="saveFeature(f.code)"
            >저장</ABtn>
          </div>
        </div>

        <AEmpty v-else title="설정을 불러오지 못했습니다" sub="새로고침을 눌러 다시 시도해 주세요." />
      </ACard>
    </template>

    <!-- ───────────────────── 프로필 ───────────────────── -->
    <template v-else-if="tab === 'profiles'">
      <ACard body-flush>
        <template #title>
          <span class="ai-set__sec-title">
            <i class="material-icons-round ai-set__sec-icon">dns</i>
            AI 서버 프로필
          </span>
        </template>
        <template #sub>등록 → 연결 테스트 → 기능에 지정 순서로 사용합니다. 삭제는 없고 "사용" 을 꺼서 대신합니다.</template>
        <template #action>
          <APill kind="info">{{ profiles.length }}개</APill>
        </template>

        <!-- 조회 실패를 "등록된 프로필이 없습니다" 로 단정하면 안 됩니다. 로딩 중에도 표를 그리지 않습니다. -->
        <AEmpty
          v-if="profilesError"
          title="프로필 목록을 불러오지 못했습니다"
          :sub="profilesError"
        />
        <AEmpty
          v-else-if="!loading && !profiles.length"
          title="등록된 프로필이 없습니다"
          sub="'프로필 등록'으로 AI 서버 접속 정보를 먼저 만드세요."
        />

        <ATable v-else-if="profiles.length" :columns="profileCols" :rows="profiles" row-key="profile_code">
          <template #cell:profile_code="{ row }">
            <div class="ai-set__pcode">
              <span class="ai-set__mono ai-set__pcode-main">{{ row.profile_code }}</span>
              <span v-if="row.description" class="ai-set__pcode-desc">{{ row.description }}</span>
            </div>
          </template>

          <template #cell:provider_type="{ row }">
            <div class="ai-set__ptype">
              <span>{{ providerLabel(row.provider_type) }}</span>
              <span class="ai-set__mono ai-set__url" :title="row.base_url">{{ row.base_url || '주소 없음' }}</span>
            </div>
          </template>

          <template #cell:api_key_env="{ row }">
            <div v-if="row.api_key_env" class="ai-set__key">
              <span class="ai-set__mono">{{ row.api_key_env }}</span>
              <APill v-if="!row.api_key_set" kind="err" dot>키 없음</APill>
            </div>
            <span v-else class="ai-set__dash">—</span>
          </template>

          <template #cell:default_model="{ row }">
            <span v-if="row.default_model" class="ai-set__mono">{{ row.default_model }}</span>
            <span v-else class="ai-set__dash">—</span>
          </template>

          <template #cell:flags="{ row }">
            <div class="ai-set__flags">
              <APill v-if="row.is_cloud" kind="warn">과금</APill>
              <APill :kind="row.is_active ? 'ok' : 'default'" dot>{{ row.is_active ? '사용' : '미사용' }}</APill>
            </div>
          </template>

          <template #cell:used_by="{ row }">
            <div v-if="row.used_by && row.used_by.length" class="ai-set__usedby">
              <APill v-for="code in row.used_by" :key="code" kind="info">{{ featureLabel(code) }}</APill>
            </div>
            <span v-else class="ai-set__dash">—</span>
          </template>

          <template #cell:test="{ row }">
            <button
              v-if="testResults[row.profile_code]"
              type="button"
              class="ai-set__test-res"
              @click="openTestDetail(row.profile_code)"
            >
              <APill :kind="testResults[row.profile_code].ok ? 'ok' : 'err'" dot>
                {{ testResults[row.profile_code].ok
                  ? `성공 ${testResults[row.profile_code].latency_ms}ms`
                  : '실패' }}
              </APill>
            </button>
            <span v-else class="ai-set__dash">—</span>
          </template>

          <template #cell:_actions="{ row }">
            <div class="ai-set__actions">
              <ABtn
                size="sm"
                kind="yellow"
                icon="network_check"
                :loading="testing === row.profile_code"
                :disabled="!!testing"
                @click="runTest(row)"
              >연결 테스트</ABtn>
              <ABtn size="sm" kind="ghost" icon="edit" icon-only title="수정" @click="openEdit(row)" />
            </div>
          </template>
        </ATable>
      </ACard>
    </template>

    <!-- ───────────────────── 상태 ───────────────────── -->
    <template v-else>
      <ACard>
        <div class="ai-set__status-bar">
          <div class="p-field ai-set__win">
            <label class="p-field__label">집계 구간</label>
            <select v-model.number="windowMin" class="p-input" @change="fetchStatus">
              <option :value="15">최근 15분</option>
              <option :value="30">최근 30분</option>
              <option :value="60">최근 60분</option>
              <option :value="180">최근 3시간</option>
              <option :value="360">최근 6시간</option>
              <option :value="720">최근 12시간</option>
              <option :value="1440">최근 24시간</option>
            </select>
          </div>
          <div class="ai-set__auto">
            <button
              type="button"
              :class="['p-switch', { 'is-on': autoRefresh }]"
              @click="toggleAutoRefresh"
            ></button>
            <span>30초마다 자동 새로고침</span>
          </div>
          <div class="ai-set__status-src">
            <span>설정 출처</span>
            <APill :kind="status && status.config_source === 'db' ? 'ok' : 'err'" dot>
              {{ status ? (status.config_source === 'db' ? '정상 (DB)' : '비상 기본값') : '—' }}
            </APill>
          </div>
          <ABtn kind="ghost" icon="refresh" :loading="statusLoading" @click="fetchStatus">새로고침</ABtn>
        </div>
      </ACard>

      <ACard body-flush>
        <template #title>
          <span class="ai-set__sec-title">
            <i class="material-icons-round ai-set__sec-icon">insights</i>
            최근 {{ status ? status.recent_window_min : windowMin }}분 호출
          </span>
        </template>
        <template #sub>기능 × 프로필 × 역할별 성공·실패 수입니다. 예비 역할에 성공이 잡히면 1순위가 죽어 전환된 상태입니다.</template>

        <!-- 조회 실패를 "호출이 없었습니다" 로 읽히게 두면 장애 중에 정상으로 오해합니다. -->
        <AEmpty
          v-if="statusError"
          title="집계를 확인하지 못했습니다"
          :sub="statusError"
        />
        <AEmpty
          v-else-if="!recentRows.length"
          title="집계된 호출이 없습니다"
          sub="이 구간에 AI 호출이 없었습니다. 구간을 넓혀 보세요."
        />
        <ATable v-else :columns="recentCols" :rows="recentRows" row-key="_key">
          <template #cell:feature="{ row }">{{ featureLabel(row.feature) }}</template>
          <template #cell:profile_code="{ row }">
            <span class="ai-set__mono">{{ row.profile_code }}</span>
          </template>
          <template #cell:role="{ row }">
            <APill :kind="row.role === 'fallback' ? 'warn' : row.role === 'test' ? 'default' : 'info'">
              {{ roleLabel(row.role) }}
            </APill>
          </template>
          <template #cell:success_count="{ row }">
            <span :class="['ai-set__mono', { 'ai-set__hl': row.role === 'fallback' && row.success_count > 0 }]">
              {{ row.success_count }}
            </span>
          </template>
          <template #cell:failed_count="{ row }">
            <span :class="['ai-set__mono', { 'ai-set__bad': row.failed_count > 0 }]">{{ row.failed_count }}</span>
          </template>
          <template #cell:last_at="{ row }">
            <span class="ai-set__mono ai-set__sm">{{ fmtClock(row.last_at) }}</span>
          </template>
        </ATable>
      </ACard>

      <ACard body-flush>
        <template #title>
          <span class="ai-set__sec-title">
            <i class="material-icons-round ai-set__sec-icon">block</i>
            자동 차단 상태
          </span>
        </template>
        <template #sub>
          같은 기능에서 같은 프로필이 연속 3회 실패하면 60초 동안 건너뜁니다.
          서버 프로세스 여러 개 중 이 요청을 받은 1개의 상태라 새로고침마다 달라질 수 있는 <b>참고용</b> 값입니다.
        </template>
        <template #action><APill kind="default">참고</APill></template>

        <AEmpty
          v-if="statusError"
          title="차단 상태를 확인하지 못했습니다"
          :sub="statusError"
        />
        <AEmpty
          v-else-if="!circuitRows.length"
          title="차단된 프로필이 없습니다"
          sub="모든 프로필이 정상 호출되고 있습니다."
        />
        <ATable v-else :columns="circuitCols" :rows="circuitRows" row-key="_key">
          <template #cell:feature="{ row }">{{ featureLabel(row.feature) }}</template>
          <template #cell:profile_code="{ row }">
            <span class="ai-set__mono">{{ row.profile_code }}</span>
          </template>
          <template #cell:state="{ row }">
            <APill :kind="row.state === 'open' ? 'err' : 'warn'" dot>
              {{ row.state === 'open' ? '차단 중' : '복구 확인 중' }}
            </APill>
          </template>
          <template #cell:open_remaining_sec="{ row }">
            <span class="ai-set__mono">
              {{ row.open_remaining_sec === null || row.open_remaining_sec === undefined
                ? '—'
                : `${Math.round(row.open_remaining_sec)}초` }}
            </span>
          </template>
        </ATable>
      </ACard>
    </template>

    <AiProfileFormModal
      v-model="showForm"
      :profile="editingProfile"
      :existing-codes="profileCodes"
      :allowed-key-envs="allowedKeyEnvs"
      :provider-types="providerTypes"
      @saved="onProfileSaved"
    />

    <AModal v-model="testDetailOpen" size="md" title="연결 테스트 결과">
      <div v-if="testDetail" class="ai-set__test-detail">
        <div class="ai-set__test-head">
          <APill :kind="testDetail.ok ? 'ok' : 'err'" size="lg" dot>
            {{ testDetail.ok ? '성공' : '실패' }}
          </APill>
          <span class="ai-set__mono">{{ testDetail.profile_code }}</span>
        </div>
        <table class="ai-set__kv">
          <tr><th>소요 시간</th><td>{{ fmtLatency(testDetail.latency_ms) }}</td></tr>
          <tr><th>응답 모델</th><td>{{ testDetail.model || '—' }}</td></tr>
          <tr v-if="!testDetail.ok"><th>원인</th><td>{{ errorTypeLabel(testDetail.error_type) || '—' }}</td></tr>
          <tr v-if="testDetail.http_status"><th>HTTP 상태</th><td>{{ testDetail.http_status }}</td></tr>
          <tr v-if="testDetail.message"><th>서버 메시지</th><td class="ai-set__sm">{{ testDetail.message }}</td></tr>
          <tr><th>테스트 시각</th><td>{{ testDetail.tested_at }}</td></tr>
        </table>
      </div>
      <template #foot>
        <ABtn kind="ghost" @click="testDetailOpen = false">닫기</ABtn>
      </template>
    </AModal>
  </div>
</template>

<script>
import swal from "sweetalert2";
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AModal from "@/components/Pineapple/AModal.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import AiProfileFormModal from "./components/AiProfileFormModal.vue";
import { apiErrorMessage } from "@/utils/apiError";
import { formatServerClock } from "@/utils/datetime";
import { FEATURES, featureLabel, providerLabel, errorTypeLabel, roleLabel, fmtLatency } from "@/utils/aiSettings";

const TABS = [
  { id: "features", label: "기능별 설정", icon: "tune" },
  { id: "profiles", label: "프로필", icon: "dns" },
  { id: "status", label: "상태", icon: "monitor_heart" },
];

/** 서버 값(숫자·null)을 입력칸이 다룰 수 있는 문자열로 바꿉니다. null 은 빈 칸 = "값 없음". */
const toInput = (v) => (v === null || v === undefined ? "" : String(v));

export default {
  name: "SystemAiSettings",
  components: { APageHead, ACard, ABtn, APill, ATable, AModal, AEmpty, AiProfileFormModal },
  data() {
    return {
      TABS,
      FEATURES,
      tab: "features",
      loading: false,
      statusLoading: false,

      features: [],
      drafts: {},
      savingFeature: "",

      profiles: [],
      profilesError: "",
      allowedKeyEnvs: [],
      providerTypes: [],
      showForm: false,
      editingProfile: null,
      testing: "",
      testResults: {},
      testDetailOpen: false,
      testDetail: null,

      status: null,
      statusError: "",
      windowMin: 60,
      autoRefresh: false,
      autoTimer: null,

      profileCols: [
        { key: "profile_code",  label: "프로필" },
        { key: "provider_type", label: "서버" },
        { key: "api_key_env",   label: "API 키 이름" },
        { key: "default_model", label: "기본 모델" },
        { key: "flags",         label: "상태", width: 130, align: "center" },
        { key: "used_by",       label: "사용 중인 기능" },
        { key: "test",          label: "최근 테스트", width: 120, align: "center" },
        { key: "_actions",      label: "작업", width: 190, align: "center" },
      ],
      recentCols: [
        { key: "feature",       label: "기능", width: 140 },
        { key: "profile_code",  label: "프로필" },
        { key: "role",          label: "역할", width: 100, align: "center" },
        { key: "success_count", label: "성공", width: 80, align: "right" },
        { key: "failed_count",  label: "실패", width: 80, align: "right" },
        { key: "last_at",       label: "마지막 호출", width: 170 },
      ],
      circuitCols: [
        { key: "feature",            label: "기능", width: 140 },
        { key: "profile_code",       label: "프로필" },
        { key: "state",              label: "상태", width: 120, align: "center" },
        { key: "fails",              label: "연속 실패", width: 90, align: "right" },
        { key: "open_remaining_sec", label: "남은 차단", width: 100, align: "right" },
      ],
    };
  },
  computed: {
    featureByCode() {
      return this.features.reduce((acc, f) => {
        acc[f.feature_code] = f;
        return acc;
      }, {});
    },
    activeProfiles() {
      // 1순위·예비 드롭다운에는 is_active=true 인 프로필만 올립니다(docs/AI설정_관리자화면_가이드.md §2-1).
      return this.profiles.filter((p) => p.is_active);
    },
    profileByCode() {
      return this.profiles.reduce((acc, p) => {
        acc[p.profile_code] = p;
        return acc;
      }, {});
    },
    profileCodes() {
      return this.profiles.map((p) => p.profile_code);
    },
    recentRows() {
      return (this.status?.recent_by_profile || []).map((r) => ({
        ...r,
        _key: `${r.feature}|${r.profile_code}|${r.role}`,
      }));
    },
    circuitRows() {
      return (this.status?.worker_circuits || []).map((r) => ({
        ...r,
        _key: `${r.feature}|${r.profile_code}`,
      }));
    },
    fallbackRunning() {
      return this.recentRows.filter((r) => r.role === "fallback" && r.success_count > 0);
    },
  },
  mounted() {
    this.reloadAll();
  },
  beforeUnmount() {
    this.stopAutoRefresh();
  },
  methods: {
    featureLabel,
    providerLabel,
    errorTypeLabel,
    roleLabel,
    fmtLatency,

    fmtClock(v) {
      return formatServerClock(v) || "—";
    },

    updatedByLabel(by) {
      return by === "migration" ? "최초 설치값" : by || "—";
    },

    async reloadAll() {
      this.loading = true;
      try {
        // 기능 설정 드롭다운이 프로필 목록에 의존하므로 둘을 함께 받습니다.
        await Promise.all([this.fetchProfiles(), this.fetchFeatures()]);
        await this.fetchStatus();
      } finally {
        this.loading = false;
      }
    },

    /**
     * @param {string} [onlyCode] 편집칸을 되돌릴 기능. 주면 그 카드만 되돌립니다.
     *   카드 3장이 한 화면에 펼쳐져 있어, 한 장을 저장할 때 나머지 장의 미저장 편집을
     *   말없이 날리지 않도록 저장 직후에는 저장한 카드만 새 값으로 맞춥니다.
     */
    async fetchFeatures(onlyCode) {
      try {
        const res = await this.$axios({ method: "get", url: "/system/ai-settings/features" });
        this.features = res.data?.items || [];
        this.features.forEach((f) => {
          const code = f.feature_code;
          if (!onlyCode || code === onlyCode || !this.drafts[code]) this.resetDraft(code);
        });
      } catch (e) {
        swal.fire("오류", apiErrorMessage(e, "기능별 AI 설정을 불러오지 못했습니다."), "error");
      }
    },

    async fetchProfiles() {
      try {
        const res = await this.$axios({ method: "get", url: "/system/ai-settings/profiles" });
        this.profiles = res.data?.items || [];
        this.allowedKeyEnvs = res.data?.allowed_key_envs || [];
        this.providerTypes = res.data?.provider_types || [];
        this.profilesError = "";
      } catch (e) {
        this.profilesError = apiErrorMessage(e, "AI 프로필 목록을 불러오지 못했습니다.");
        swal.fire("오류", this.profilesError, "error");
      }
    },

    async fetchStatus() {
      this.statusLoading = true;
      try {
        const res = await this.$axios({
          method: "get",
          url: "/system/ai-settings/status",
          params: { windowMin: this.windowMin },
        });
        this.status = res.data;
        this.statusError = "";
      } catch (e) {
        console.error("AI 설정 상태 조회 실패:", e);
        // 설정 DB 를 못 읽는 상황이면 이 엔드포인트도 흔들립니다. 조용히 넘기면
        // bootstrap 경고·예비 전환 배너가 "문제 없음" 과 구분되지 않으므로 실패 사실을 띄웁니다.
        this.status = null;
        this.statusError = apiErrorMessage(e, "상태를 불러오지 못했습니다.");
      } finally {
        this.statusLoading = false;
      }
    },

    // ── 기능별 설정 ─────────────────────────────────────────
    draft(code) {
      return this.drafts[code];
    },

    resetDraft(code) {
      const f = this.featureByCode[code];
      if (!f) return;
      this.drafts[code] = {
        primary_profile: f.primary_profile || "",
        fallback_profile: f.fallback_profile || "",
        model: f.model || "",
        temperature: toInput(f.temperature),
        max_tokens: toInput(f.max_tokens),
        timeout_sec: toInput(f.timeout_sec),
        allow_cloud: !!f.allow_cloud,
        updated_at: f.updated_at,
        updated_by: f.updated_by,
      };
    },

    /**
     * 1순위를 바꾸면 서버가 model 을 null 로 되돌립니다(옛 서버의 모델명이 새 서버로 나가는 것을 막기 위해).
     * 화면도 같은 순간 모델칸을 비워 두 값이 어긋나지 않게 합니다(docs/AI설정_관리자화면_가이드.md §2-1).
     * 단 원래 1순위로 되돌린 경우에는 원래 모델도 같이 되돌립니다.
     * 그러지 않으면 "바꿨다가 되돌렸을 뿐" 인데 model 만 null 로 저장돼 지정해 둔 모델명이 사라집니다.
     */
    onPrimaryChange(code, value) {
      const d = this.drafts[code];
      if (!d) return;
      const orig = this.featureByCode[code];
      if (d.primary_profile !== value) {
        d.model = value === orig?.primary_profile ? orig.model || "" : "";
      }
      d.primary_profile = value;
      // 1순위와 같은 값은 예비로 둘 수 없습니다(422). 말없이 지우면 장애 대비용 예비 지정이
      // 사라진 것을 모른 채 저장하게 되므로 반드시 알립니다.
      if (d.fallback_profile === value) {
        d.fallback_profile = "";
        this.$notifyVue({
          type: "warn",
          title: "예비 프로필 지정을 해제했습니다.",
          text: "1순위와 같은 프로필은 예비로 쓸 수 없습니다. 다른 예비 프로필을 골라 주세요.",
        });
      }
    },

    /**
     * 드롭다운 선택지. 활성 프로필만 올리되, 지금 지정돼 있는데 목록에 없는 값(프로필 조회 실패 등)은
     * 표시용으로 남깁니다. 빼 버리면 select 가 첫 옵션을 고른 것처럼 보여, 실제 저장된 설정과
     * 화면이 어긋나고 "예비 없음" 으로 잘못 읽히게 됩니다.
     */
    profileOptions(code, field) {
      const d = this.drafts[code] || {};
      const list = this.activeProfiles.filter(
        (p) => field === "primary" || p.profile_code !== d.primary_profile
      );
      const current = field === "primary" ? d.primary_profile : d.fallback_profile;
      if (current && !list.some((p) => p.profile_code === current)) {
        // 과금·키 여부를 모르는 값입니다. is_cloud 를 false 로 단정하면 "과금 아님" 으로 보여
        // 잘못된 안심을 주므로, 값을 채우지 않고 라벨로 "확인 불가" 를 알립니다.
        return [{ profile_code: current, _unknown: true }, ...list];
      }
      return list;
    },

    /** 과금이 차단된 기능에 과금 프로필을 고르면 409 입니다. 미리 선택을 막습니다. */
    isProfileBlocked(code, profile) {
      return !this.drafts[code]?.allow_cloud && profile.is_cloud;
    },

    profileOptionLabel(code, p) {
      const parts = [p.profile_code];
      if (p._unknown) parts.push("현재 지정값 — 프로필 목록에 없어 과금 여부 확인 불가");
      if (p.is_cloud) parts.push(this.isProfileBlocked(code, p) ? "과금 — 선택 불가" : "과금");
      if (!p.api_key_set && p.api_key_env) parts.push("키 없음");
      return parts.length > 1 ? `${parts[0]} (${parts.slice(1).join(", ")})` : parts[0];
    },

    primaryDefaultModel(code) {
      const p = this.profileByCode[this.drafts[code]?.primary_profile];
      return p?.default_model ? `${p.default_model} (프로필 기본값)` : "프로필 기본 모델";
    },

    /**
     * allow_cloud 를 끄려는데 이미 과금 프로필이 지정돼 있으면 서버가 409 로 거부합니다.
     * 저장 버튼을 누르기 전에 무엇을 바꿔야 하는지 알려 줍니다.
     */
    cloudConflict(code) {
      const d = this.drafts[code];
      if (!d || d.allow_cloud) return "";
      const hits = [d.primary_profile, d.fallback_profile]
        .filter(Boolean)
        .map((c) => this.profileByCode[c])
        .filter((p) => p && p.is_cloud)
        .map((p) => p.profile_code);
      if (!hits.length) return "";
      return `과금 프로필(${[...new Set(hits)].join(", ")})이 지정돼 있어 "과금 프로필 사용 허용" 을 끌 수 없습니다. 먼저 다른 프로필로 바꾸세요.`;
    },

    /**
     * 서버는 "보낸 필드만" 바꾸고 필드마다 null 의 뜻이 다릅니다(§2-1).
     * 그래서 전체 폼을 보내지 않고 바뀐 필드만 골라 보냅니다.
     * - fallback_profile·model·temperature·max_tokens: null = 값 비우기
     * - primary_profile·timeout_sec·allow_cloud: null 은 무시되므로 값이 있을 때만 담습니다
     */
    buildFeaturePatch(code) {
      const orig = this.featureByCode[code];
      const d = this.drafts[code];
      if (!orig || !d) return {};
      const body = {};

      if (d.primary_profile && d.primary_profile !== orig.primary_profile) {
        body.primary_profile = d.primary_profile;
      }

      const fallback = d.fallback_profile || null;
      if (fallback !== (orig.fallback_profile ?? null)) body.fallback_profile = fallback;

      // 1순위를 바꾸면서 model 키를 빼면 서버가 model 을 null 로 되돌립니다(§2-1).
      // 화면 값이 원본과 같아 diff 에 안 걸리는 경우에도, 1순위가 바뀌는 요청에는 model 을 반드시 함께 싣습니다.
      const model = d.model || null;
      if (model !== (orig.model ?? null) || "primary_profile" in body) body.model = model;

      const temperature = d.temperature === "" ? null : Number(d.temperature);
      if (temperature !== (orig.temperature ?? null)) body.temperature = temperature;

      const maxTokens = d.max_tokens === "" ? null : Number(d.max_tokens);
      if (maxTokens !== (orig.max_tokens ?? null)) body.max_tokens = maxTokens;

      const timeout = d.timeout_sec === "" ? null : Number(d.timeout_sec);
      if (timeout !== null && timeout !== orig.timeout_sec) body.timeout_sec = timeout;

      if (d.allow_cloud !== !!orig.allow_cloud) body.allow_cloud = d.allow_cloud;

      return body;
    },

    isDirty(code) {
      return Object.keys(this.buildFeaturePatch(code)).length > 0;
    },

    /** 보내려는 필드의 범위를 검사합니다. 문제가 있으면 안내 문구를, 없으면 빈 문자열을 돌려줍니다. */
    validatePatch(body) {
      const num = (v) => (typeof v === "number" ? v : NaN);
      if ("timeout_sec" in body) {
        const v = num(body.timeout_sec);
        if (!Number.isInteger(v) || v < 5 || v > 600) return "응답 대기 시간은 5~600초 사이의 정수여야 합니다.";
      }
      if ("temperature" in body && body.temperature !== null) {
        const v = num(body.temperature);
        if (Number.isNaN(v) || v < 0 || v > 2) return "temperature 는 0~2 사이여야 합니다.";
        // 0.07 * 100 이 7.000000000000001 이 되는 부동소수 오차를 흡수합니다.
        if (Math.abs(Math.round(v * 100) - v * 100) > 1e-9) {
          return "temperature 는 소수 2자리까지만 입력할 수 있습니다.";
        }
      }
      if ("max_tokens" in body && body.max_tokens !== null) {
        const v = num(body.max_tokens);
        if (!Number.isInteger(v) || v < 1 || v > 32000) return "max_tokens 는 1~32000 사이의 정수여야 합니다.";
      }
      return "";
    },

    async saveFeature(code) {
      const body = this.buildFeaturePatch(code);
      if (!Object.keys(body).length) {
        this.$notifyVue({ type: "warn", title: "변경된 내용이 없습니다." });
        return;
      }
      // timeout_sec 는 비울 수 없는 값이라 빈 칸은 patch 에서 빠지고 서버가 무시합니다.
      // 아무 말 없이 옛 값이 되돌아오면 "왜 안 바뀌지?" 가 되므로 먼저 알립니다.
      if (this.drafts[code]?.timeout_sec === "") {
        this.$notifyVue({
          type: "warn",
          title: "응답 대기 시간이 비어 있어 그대로 둡니다.",
          text: "비울 수 없는 값입니다. 바꾸려면 5~600초 사이의 값을 넣고 다시 저장해 주세요.",
        });
      }

      // 서버의 422 는 영문 배열 형식이라 읽기 어렵습니다. 범위를 화면에서 먼저 거릅니다(§1-4).
      // 검사는 "이번에 보내는 필드" 에만 겁니다. 안 건드린 칸 때문에 저장이 막히면 안 되기 때문입니다.
      const invalid = this.validatePatch(body);
      if (invalid) {
        this.$notifyVue({ type: "error", title: invalid });
        return;
      }

      this.savingFeature = code;
      try {
        const res = await this.$axios({
          method: "put",
          url: `/system/ai-settings/features/${code}`,
          data: body,
          loadingBar: true,
        });
        const sec = res.data?.applies_within_sec;
        this.$notifyVue({
          type: "success",
          title: `${featureLabel(code)} 설정을 저장했습니다.`,
          text: sec ? `실제 AI 호출에는 최대 ${sec}초 안에 반영됩니다.` : undefined,
        });
        await this.fetchFeatures(code);
        // 기능이 프로필을 쓰기 시작·중단하면 used_by 가 달라집니다.
        await this.fetchProfiles();
      } catch (e) {
        swal.fire("저장 실패", apiErrorMessage(e, "설정을 저장하지 못했습니다."), "error");
      } finally {
        this.savingFeature = "";
      }
    },

    // ── 프로필 ─────────────────────────────────────────────
    openCreate() {
      this.editingProfile = null;
      this.showForm = true;
    },

    openEdit(row) {
      this.editingProfile = row;
      this.showForm = true;
    },

    async onProfileSaved() {
      await this.fetchProfiles();
      // 프로필을 고치면 used_by·활성 여부가 달라지므로 기능 설정도 다시 받되,
      // 편집 중인 카드는 건드리지 않습니다(어느 기능 코드와도 겹치지 않는 값을 넘깁니다).
      await this.fetchFeatures("__keep_drafts__");
    },

    /**
     * 연결 테스트는 실제 AI 호출 1건을 내보냅니다. 과금 프로필이면 소액이지만 과금되므로 확인을 받습니다.
     * HTTP 상태는 실패해도 200 이라 본문의 ok 로 성패를 판정합니다(§2-3).
     */
    async runTest(row) {
      if (row.is_cloud) {
        const ok = await swal.fire({
          icon: "warning",
          title: "과금 프로필입니다",
          text: `${row.profile_code} 로 실제 호출 1건이 발생하며 소액 과금됩니다. 진행할까요?`,
          showCancelButton: true,
          confirmButtonText: "테스트 실행",
          cancelButtonText: "취소",
        });
        if (!ok.isConfirmed) return;
      }

      this.testing = row.profile_code;
      try {
        const res = await this.$axios({
          method: "post",
          url: `/system/ai-settings/profiles/${row.profile_code}/test`,
        });
        const r = res.data || {};
        this.testResults[row.profile_code] = {
          ...r,
          profile_code: row.profile_code,
          tested_at: formatServerClock(new Date().toISOString()),
        };
        if (r.ok) {
          this.$notifyVue({
            type: "success",
            title: `${row.profile_code} 연결 성공 (${r.latency_ms}ms)`,
            text: r.model ? `응답 모델: ${r.model}` : undefined,
          });
        } else {
          swal.fire({
            icon: "error",
            title: `${row.profile_code} 연결 실패`,
            text: errorTypeLabel(r.error_type) || "원인을 알 수 없습니다.",
            footer: r.message ? `서버 메시지: ${r.message}` : undefined,
          });
        }
      } catch (e) {
        swal.fire("테스트 실패", apiErrorMessage(e, "연결 테스트를 실행하지 못했습니다."), "error");
      } finally {
        this.testing = "";
      }
    },

    openTestDetail(code) {
      this.testDetail = this.testResults[code];
      this.testDetailOpen = true;
    },

    // ── 상태 자동 새로고침 ──────────────────────────────────
    toggleAutoRefresh() {
      this.autoRefresh = !this.autoRefresh;
      if (this.autoRefresh) {
        this.autoTimer = setInterval(this.fetchStatus, 30000);
      } else {
        this.stopAutoRefresh();
      }
    },

    stopAutoRefresh() {
      if (this.autoTimer) {
        clearInterval(this.autoTimer);
        this.autoTimer = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.ai-set {
  display: flex;
  flex-direction: column;
  gap: 18px;

  &__tabs { align-self: flex-start; }
  &__tab-icon { font-size: 14px !important; line-height: 1; }

  &__banner {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 14px 18px;
    border-radius: $p-radius-md;

    > i { font-size: 20px; flex-shrink: 0; }

    > div {
      display: flex;
      flex-direction: column;
      gap: 3px;
    }

    b { font-size: 13px; }
    span { font-size: 12px; line-height: 1.6; }

    &--err {
      background: $p-err-soft;
      border-left: 3px solid $p-err;
      color: $p-err-ink;
    }

    &--warn {
      background: $p-warn-soft;
      border-left: 3px solid $p-warn;
      color: $p-warn-ink;
    }
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 48px 0;
    color: $p-ink-mid;
    font-size: 13px;
  }

  &__sec-title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  &__sec-icon {
    font-size: 18px !important;
    color: $p-point-deep;
  }

  &__updated {
    font-size: 11px;
    color: $p-ink-soft;
    font-family: $p-font-mono;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;

    @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 700px)  { grid-template-columns: 1fr; }
  }

  &__toggle {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 14px 16px;
    background: $p-panel-alt;
    border-radius: $p-radius-md;

    > div {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    b { font-size: 13px; color: $p-ink; }
    span { font-size: 11px; color: $p-ink-mid; line-height: 1.5; }
  }

  &__alert {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 12px 14px;
    border-radius: $p-radius-md;
    font-size: 12px;
    line-height: 1.6;

    > i { font-size: 18px; flex-shrink: 0; }

    &--warn { background: $p-warn-soft; border-left: 3px solid $p-warn; color: $p-warn-ink; }
  }

  &__form-foot {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }

  &__mono {
    font-family: $p-font-mono;
    font-size: 12px;
  }

  &__sm { font-size: 11px; }

  &__dash { color: $p-ink-softer; }

  &__hl {
    color: $p-warn-ink;
    font-weight: 800;
  }

  &__bad {
    color: $p-err-ink;
    font-weight: 800;
  }

  &__pcode {
    display: flex;
    flex-direction: column;
    gap: 2px;

    &-main { font-weight: 700; color: $p-ink; }
    &-desc { font-size: 11px; color: $p-ink-mid; }
  }

  &__ptype {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 12px;
  }

  &__url {
    font-size: 11px;
    color: $p-ink-soft;
    max-width: 260px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__key,
  &__flags,
  &__usedby,
  &__actions {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  &__flags,
  &__actions {
    justify-content: center;
  }

  &__test-res {
    border: 0;
    background: transparent;
    padding: 0;
    cursor: pointer;
  }

  &__status-bar {
    display: flex;
    align-items: flex-end;
    gap: 16px;
    flex-wrap: wrap;
  }

  &__win { width: 160px; }

  &__auto {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: $p-ink-mid;
    padding-bottom: 8px;
  }

  &__status-src {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: $p-ink-mid;
    padding-bottom: 8px;
    margin-left: auto;
  }

  &__test-detail {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  &__test-head {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__kv {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;

    th, td {
      padding: 8px 12px;
      border: 1px solid $p-line;
      text-align: left;
      word-break: break-word;
    }

    th {
      background: $p-panel-alt;
      color: $p-ink-mid;
      font-weight: 800;
      width: 32%;
    }
  }
}
</style>
